import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Settings, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { faqService, faqCategoryService } from '../../../services/faqService';
import toast from 'react-hot-toast';

// Module configuration with colors
const MODULE_CONFIG = {
  patient: { label: 'Patient', color: 'blue', borderColor: 'border-blue-200', textColor: 'text-blue-600', bgColor: 'bg-blue-50', hoverBg: 'hover:bg-blue-50' },
  admin: { label: 'Admin', color: 'green', borderColor: 'border-green-200', textColor: 'text-green-600', bgColor: 'bg-green-50', hoverBg: 'hover:bg-green-50' },
  doctor: { label: 'Doctor', color: 'purple', borderColor: 'border-purple-200', textColor: 'text-purple-600', bgColor: 'bg-purple-50', hoverBg: 'hover:bg-purple-50' },
  clinician: { label: 'Clinician', color: 'orange', borderColor: 'border-orange-200', textColor: 'text-orange-600', bgColor: 'bg-orange-50', hoverBg: 'hover:bg-orange-50' }
};

const AdminFaqsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Admin FAQs';
  }, []);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // FAQ data from API
  const [patientFaqData, setPatientFaqData] = useState([]);
  const [adminFaqData, setAdminFaqData] = useState([]);
  const [doctorFaqData, setDoctorFaqData] = useState([]);
  const [clinicianFaqData, setClinicianFaqData] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  
  // View All Modal state
  const [showViewAllModal, setShowViewAllModal] = useState(false);
  const [viewAllModule, setViewAllModule] = useState(null);
  const [viewAllFaqs, setViewAllFaqs] = useState([]);
  const [viewAllLoading, setViewAllLoading] = useState(false);
  const [expandedFaqIds, setExpandedFaqIds] = useState([]);
  
  const categoryDropdownRef = useRef(null);

  // Fetch FAQs from API
  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const [patientRes, adminRes, doctorRes, clinicianRes, categoriesRes] = await Promise.all([
        faqService.getByModule('patient', { limit: 10 }),
        faqService.getByModule('admin', { limit: 10 }),
        faqService.getByModule('doctor', { limit: 10 }),
        faqService.getByModule('clinician', { limit: 10 }),
        faqCategoryService.getAll({ is_active: true })
      ]);
      
      setPatientFaqData(patientRes.faqs || []);
      setAdminFaqData(adminRes.faqs || []);
      setDoctorFaqData(doctorRes.faqs || []);
      setClinicianFaqData(clinicianRes.faqs || []);
      setAllCategories(categoriesRes.items || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  // Filter FAQs based on selected categories and search
  const filterFaqs = (faqs) => {
    return faqs.filter(faq => {
      const matchesCategory = selectedCategories.length === 0 || 
        selectedCategories.includes(faq.category_name);
      const matchesSearch = !searchQuery || 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  };

  const handleViewAll = async (moduleType) => {
    const module = moduleType.toLowerCase();
    setViewAllModule(module);
    setShowViewAllModal(true);
    setViewAllLoading(true);
    setExpandedFaqIds([]);
    
    try {
      const response = await faqService.getByModule(module);
      setViewAllFaqs(response.faqs || []);
    } catch (error) {
      console.error(`Error fetching ${module} FAQs:`, error);
      toast.error(`Failed to load ${moduleType} FAQs`);
      setViewAllFaqs([]);
    } finally {
      setViewAllLoading(false);
    }
  };

  const handleCloseViewAll = () => {
    setShowViewAllModal(false);
    setViewAllModule(null);
    setViewAllFaqs([]);
    setExpandedFaqIds([]);
  };

  const toggleFaqExpand = (faqId) => {
    setExpandedFaqIds(prev => 
      prev.includes(faqId) 
        ? prev.filter(id => id !== faqId)
        : [...prev, faqId]
    );
  };

  const handleAddCategory = () => {
    setShowCategoryDropdown(!showCategoryDropdown);
  };

  const handleCategorySelect = (category) => {
    if (!selectedCategories.includes(category)) {
      setSelectedCategories([...selectedCategories, category]);
    }
    setShowCategoryDropdown(false);
  };

  const handleCategoryRemove = (categoryToRemove) => {
    setSelectedCategories(selectedCategories.filter(cat => cat !== categoryToRemove));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get unique categories from all FAQs
  const uniqueCategories = [...new Set([
    ...patientFaqData.map(f => f.category_name),
    ...adminFaqData.map(f => f.category_name),
    ...doctorFaqData.map(f => f.category_name),
    ...clinicianFaqData.map(f => f.category_name)
  ])].filter(Boolean);

  const filteredPatientFaqs = filterFaqs(patientFaqData);
  const filteredAdminFaqs = filterFaqs(adminFaqData);
  const filteredDoctorFaqs = filterFaqs(doctorFaqData);
  const filteredClinicianFaqs = filterFaqs(clinicianFaqData);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-2 px-6 pb-6">
        {/* Header with Management Link */}
        <div className="flex justify-between items-center mb-4">
          <div></div>
          <Link
            to="/admin/faq-management"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Settings className="h-4 w-4" />
            Manage FAQs
          </Link>
        </div>

        {/* First Block - Filter Section */}
        <div className="bg-white border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex gap-6">
            {/* Filter Category - 30% */}
            <div className="w-[30%]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter Category:
              </label>
              <div className="relative" ref={categoryDropdownRef}>
                <div className="flex items-center border border-gray-300 rounded-md bg-white">
                  <div className="flex-1 px-3 py-2 min-h-[40px] flex flex-wrap gap-1 items-center">
                    {selectedCategories.length === 0 ? (
                      <span className="text-gray-400 text-sm">Please Select</span>
                    ) : (
                      selectedCategories.map((category) => (
                        <span
                          key={category}
                          className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                        >
                          {category}
                          <button
                            onClick={() => handleCategoryRemove(category)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <button
                    onClick={handleAddCategory}
                    className="p-2 text-blue-600 hover:text-blue-800 transition-colors border-l border-gray-300"
                    title="Add Category"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Category Dropdown */}
                {showCategoryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {uniqueCategories.map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect(category)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                        disabled={selectedCategories.includes(category)}
                      >
                        {category}
                      </button>
                    ))}
                    {uniqueCategories.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">No categories available</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* I want to - 50% */}
            <div className="w-[50%]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                I want to:
              </label>
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Refresh Button */}
            <div className="flex items-end">
              <button
                onClick={fetchFaqs}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh FAQs"
              >
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading FAQs...</div>
        ) : (
          /* FAQ Blocks Grid - 2x2 */
          <div className="grid grid-cols-2 gap-6">
            {/* Patient FAQs Block */}
            <div className="bg-white border border-blue-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-blue-600 mb-2">Patient FAQs</h2>
              <div className="border-b-2 border-blue-600 mb-4"></div>
              <div className="space-y-4">
                {filteredPatientFaqs.slice(0, 3).map((faq) => (
                  <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                    <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                    <p className="text-gray-600 text-sm">{faq.answer}</p>
                  </div>
                ))}
                {filteredPatientFaqs.length === 0 && (
                  <p className="text-gray-500 text-sm">No FAQs found</p>
                )}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => handleViewAll('Patient')}
                  className="text-blue-600 hover:text-blue-800 font-medium text-sm border border-blue-600 px-4 py-2 rounded hover:bg-blue-50 transition-colors"
                >
                  View All
                </button>
              </div>
            </div>

            {/* Admin FAQs Block */}
            <div className="bg-white border border-green-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-green-600 mb-2">Admin FAQs</h2>
              <div className="border-b-2 border-green-600 mb-4"></div>
              <div className="space-y-4">
                {filteredAdminFaqs.slice(0, 3).map((faq) => (
                  <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                    <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                    <p className="text-gray-600 text-sm">{faq.answer}</p>
                  </div>
                ))}
                {filteredAdminFaqs.length === 0 && (
                  <p className="text-gray-500 text-sm">No FAQs found</p>
                )}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => handleViewAll('Admin')}
                  className="text-green-600 hover:text-green-800 font-medium text-sm border border-green-600 px-4 py-2 rounded hover:bg-green-50 transition-colors"
                >
                  View All
                </button>
              </div>
            </div>

            {/* Doctor FAQs Block */}
            <div className="bg-white border border-purple-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-purple-600 mb-2">Doctor FAQs</h2>
              <div className="border-b-2 border-purple-600 mb-4"></div>
              <div className="space-y-4">
                {filteredDoctorFaqs.slice(0, 3).map((faq) => (
                  <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                    <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                    <p className="text-gray-600 text-sm">{faq.answer}</p>
                  </div>
                ))}
                {filteredDoctorFaqs.length === 0 && (
                  <p className="text-gray-500 text-sm">No FAQs found</p>
                )}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => handleViewAll('Doctor')}
                  className="text-purple-600 hover:text-purple-800 font-medium text-sm border border-purple-600 px-4 py-2 rounded hover:bg-purple-50 transition-colors"
                >
                  View All
                </button>
              </div>
            </div>

            {/* Clinician FAQs Block */}
            <div className="bg-white border border-orange-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-orange-600 mb-2">Clinician FAQs</h2>
              <div className="border-b-2 border-orange-600 mb-4"></div>
              <div className="space-y-4">
                {filteredClinicianFaqs.slice(0, 3).map((faq) => (
                  <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                    <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                    <p className="text-gray-600 text-sm">{faq.answer}</p>
                  </div>
                ))}
                {filteredClinicianFaqs.length === 0 && (
                  <p className="text-gray-500 text-sm">No FAQs found</p>
                )}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => handleViewAll('Clinician')}
                  className="text-orange-600 hover:text-orange-800 font-medium text-sm border border-orange-600 px-4 py-2 rounded hover:bg-orange-50 transition-colors"
                >
                  View All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-800 h-16"></div>

      {/* View All Modal */}
      {showViewAllModal && viewAllModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className={`flex justify-between items-center p-4 border-b ${MODULE_CONFIG[viewAllModule]?.borderColor || 'border-gray-200'}`}>
              <h3 className={`text-xl font-bold ${MODULE_CONFIG[viewAllModule]?.textColor || 'text-gray-800'}`}>
                {MODULE_CONFIG[viewAllModule]?.label || viewAllModule} FAQs ({viewAllFaqs.length})
              </h3>
              <button
                onClick={handleCloseViewAll}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-all duration-150"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {viewAllLoading ? (
                <div className="text-center py-8 text-gray-500">Loading FAQs...</div>
              ) : viewAllFaqs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No FAQs found for this module.</div>
              ) : (
                <div className="space-y-3">
                  {viewAllFaqs.map((faq) => (
                    <div 
                      key={faq.id} 
                      className={`border rounded-lg overflow-hidden ${MODULE_CONFIG[viewAllModule]?.borderColor || 'border-gray-200'}`}
                    >
                      <button
                        onClick={() => toggleFaqExpand(faq.id)}
                        className={`w-full flex justify-between items-center p-4 text-left hover:${MODULE_CONFIG[viewAllModule]?.bgColor || 'bg-gray-50'} transition-colors`}
                      >
                        <div className="flex-1 pr-4">
                          <h4 className="font-medium text-gray-800">{faq.question}</h4>
                          {faq.category_name && (
                            <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded ${MODULE_CONFIG[viewAllModule]?.bgColor || 'bg-gray-100'} ${MODULE_CONFIG[viewAllModule]?.textColor || 'text-gray-600'}`}>
                              {faq.category_name}
                            </span>
                          )}
                        </div>
                        {expandedFaqIds.includes(faq.id) ? (
                          <ChevronUp className={`h-5 w-5 ${MODULE_CONFIG[viewAllModule]?.textColor || 'text-gray-500'}`} />
                        ) : (
                          <ChevronDown className={`h-5 w-5 ${MODULE_CONFIG[viewAllModule]?.textColor || 'text-gray-500'}`} />
                        )}
                      </button>
                      {expandedFaqIds.includes(faq.id) && (
                        <div className={`px-4 pb-4 pt-2 border-t ${MODULE_CONFIG[viewAllModule]?.borderColor || 'border-gray-200'} ${MODULE_CONFIG[viewAllModule]?.bgColor || 'bg-gray-50'}`}>
                          <p className="text-gray-600 text-sm whitespace-pre-wrap">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={handleCloseViewAll}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:border-gray-400 hover:shadow-sm active:bg-gray-200 transition-all duration-200"
              >
                Close
              </button>
              <Link
                to="/admin/faq-management"
                onClick={handleCloseViewAll}
                className={`px-4 py-2 text-white rounded-lg transition-all duration-200 ${
                  viewAllModule === 'patient' ? 'bg-blue-600 hover:bg-blue-700 hover:shadow-md active:bg-blue-800' :
                  viewAllModule === 'admin' ? 'bg-green-600 hover:bg-green-700 hover:shadow-md active:bg-green-800' :
                  viewAllModule === 'doctor' ? 'bg-purple-600 hover:bg-purple-700 hover:shadow-md active:bg-purple-800' :
                  viewAllModule === 'clinician' ? 'bg-orange-600 hover:bg-orange-700 hover:shadow-md active:bg-orange-800' :
                  'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Manage FAQs
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFaqsPage;
